/**
 * parentTheme — the web-app (parent portal) live view of the parent-chosen
 * "Sunny Light" design flag (`lwa_light_theme_v1`, signed envelope, DEFAULT OFF).
 *
 * The parent portal is already a LIGHT surface; when the parent turns Sunny
 * Light ON it should shift from the generic Apple-grey light palette to the
 * owner-approved Sunny mockup tokens (soft off-white canvas + deep-navy ink +
 * indigo accent), so the design language matches the child app and the mockup.
 * OFF stays byte-identical to today's portal.
 *
 * web-app CANNOT import web-child (`@child`), so unlike web-child's shellTheme
 * this reads the flag through ui-shared's `isLightThemeEnabled` (byte-compatible
 * signed envelope) and subscribes to the same cross-app `LIGHT_THEME_EVENT`
 * (dispatched by ui-shared setLightThemeEnabled) plus the `storage`/`focus`
 * events so a mounted portal re-renders when the parent toggles the flag —
 * including from another same-origin tab (the child app).
 */
import { useEffect, useState } from 'react';
import { isLightThemeEnabled, LIGHT_THEME_EVENT, sunnyPalette } from '@learnwithai/ui-shared';

/** Semantic palette shape the parent pages alias (classic OR sunny). */
export interface ParentPalette {
  /** Page canvas. */
  bg: string;
  /** Card / raised surface. */
  surface: string;
  /** Tinted/elevated surface (chips, alt rows). */
  surfaceAlt: string;
  /** Primary ink. */
  ink: string;
  /** Secondary ink. */
  inkSoft: string;
  /** Muted ink. */
  inkFaint: string;
  /** Hairline border. */
  border: string;
  /** Brand accent (links / primary buttons). */
  accent: string;
}

/** Today's portal palette — the generic light look (byte-identical when OFF). */
export const CLASSIC_PARENT: ParentPalette = {
  bg: '#F5F5F7',
  surface: '#FFFFFF',
  surfaceAlt: '#F3F0FF',
  ink: '#1D1D1F',
  inkSoft: '#6E6E73',
  inkFaint: '#8E8E93',
  border: '#E5E5EA',
  accent: '#007AFF',
};

/** Sunny Light portal palette — owner-approved mockup tokens (from ui-shared). */
export const SUNNY_PARENT: ParentPalette = {
  bg: sunnyPalette.bg,
  surface: sunnyPalette.surface,
  surfaceAlt: sunnyPalette.surfaceHigh,
  ink: sunnyPalette.text,
  inkSoft: sunnyPalette.textSub,
  inkFaint: sunnyPalette.textMuted,
  border: sunnyPalette.border,
  accent: sunnyPalette.indigo,
};

/** Synchronous read of the parent-signed Sunny flag (DEFAULT OFF). */
export function isSunnyParent(): boolean {
  try {
    return isLightThemeEnabled();
  } catch {
    return false;
  }
}

/**
 * Reactive read: `{ sunny, P }` where P is the active parent palette. Re-renders
 * on the theme-change event, cross-tab storage writes and window focus.
 */
export function useSunnyParent(): { sunny: boolean; P: ParentPalette } {
  const [sunny, setSunny] = useState<boolean>(() => isSunnyParent());
  useEffect(() => {
    const refresh = () => setSunny(isSunnyParent());
    try {
      window.addEventListener(LIGHT_THEME_EVENT, refresh);
      window.addEventListener('storage', refresh);
      window.addEventListener('focus', refresh);
    } catch { /* SSR/tests without a window */ }
    return () => {
      try {
        window.removeEventListener(LIGHT_THEME_EVENT, refresh);
        window.removeEventListener('storage', refresh);
        window.removeEventListener('focus', refresh);
      } catch { /* ignore */ }
    };
  }, []);
  return { sunny, P: sunny ? SUNNY_PARENT : CLASSIC_PARENT };
}
